import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { resolveAppsModuleUrl } from '../../utils/apps-module-url';
import { PrintStationRuntimeService } from './print-station-runtime.service';

/**
 * Indicador de la ticketera en el encabezado. Solo aparece en la PC registrada como estación de impresión y es quien
 * mantiene viva la impresión automática mientras el panel esté abierto, en cualquier pantalla. El color indica si
 * imprime, si no responde el micro «ticketera» o si hubo un error; al pulsarlo abre la configuración de la impresora.
 */
@Component({
  selector: 'app-print-station-indicator',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe],
  template: `
    @if (runtime.stationId()) {
      <button type="button" (click)="open()"
        class="relative flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        [attr.aria-label]="('billingApp.printing.indicator.' + runtime.state()) | transloco"
        [title]="('billingApp.printing.indicator.' + runtime.state()) | transloco">
        <i class="bi bi-printer text-lg" aria-hidden="true"></i>
        <span class="absolute right-1 top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-gray-900" [class]="dotClass()" aria-hidden="true"></span>
      </button>
    }
  `,
})
export class PrintStationIndicatorComponent {
  protected readonly runtime = inject(PrintStationRuntimeService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly router = inject(Router);

  readonly dotClass = computed(() => {
    switch (this.runtime.state()) {
      case 'running': return 'bg-success-500';
      case 'standby': return 'bg-gray-400';
      case 'agent-missing':
      case 'error': return 'bg-error-500';
      default: return 'bg-gray-300';
    }
  });

  constructor() {
    effect(() => {
      const companyId = this.companyContext.companyId();
      untracked(() => {
        if (companyId) this.runtime.start(companyId);
        else this.runtime.stop();
      });
    });
  }

  open(): void {
    void this.router.navigateByUrl(resolveAppsModuleUrl(this.router, 'facturacion') + '/impresora');
  }
}
